import { ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import SubtleBadge from '@/components/common/SubtleBadge';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import { toPersianDigit } from '@/helpers/utils';
import type {
  EeatAnalysisData,
  EeatDimensionItem,
  EeatDimensionKey,
  SeoPriorityTone
} from '@/app/ContentTools/types/aiContentAnalysisApiTypes';

const DIMENSION_ICONS: Record<EeatDimensionKey, IconProp> = {
  experience: 'compass',
  expertise: 'graduation-cap',
  authoritativeness: 'award',
  trustworthiness: 'user-shield'
};

const DIMENSION_HELP: Record<EeatDimensionKey, string> = {
  experience: 'میزان تجربه عملی و شواهد دست‌اول در محتوا.',
  expertise: 'سطح تخصص و دانش تخصصی نویسنده یا برند در موضوع صفحه.',
  authoritativeness: 'اعتبار منبع و جایگاه آن در موضوع مورد بحث.',
  trustworthiness: 'اعتمادپذیری صفحه از نظر شفافیت، منابع و صحت اطلاعات.'
};

const progressVariant = (tone: SeoPriorityTone) => {
  if (tone === 'success') return 'success';
  if (tone === 'info') return 'info';
  if (tone === 'warning') return 'warning';
  return 'danger';
};

const EeatDimensionRow = ({ item }: { item: EeatDimensionItem }) => (
  <div className="eeat-dimension-row" data-tone={item.tone}>
    <div className="d-flex align-items-center gap-2 mb-2">
      <span
        className={`d-flex align-items-center justify-content-center rounded-circle bg-soft-${item.tone} text-${item.tone} flex-shrink-0 eeat-dimension-row__icon`}
      >
        <FontAwesomeIcon icon={DIMENSION_ICONS[item.key]} className="fs--2" />
      </span>
      <span className="d-inline-flex align-items-center min-w-0 flex-grow-1 fw-semi-bold fs--1 text-700">
        <span className="text-truncate">{item.label}</span>
        <HelpTooltip placement="top" html={DIMENSION_HELP[item.key]} />
      </span>
      <div className="d-flex align-items-center gap-2 flex-shrink-0">
        <span className="fs--2 fw-semi-bold text-600">
          {toPersianDigit(`${item.score}`)} / ۱۰۰
        </span>
        <SubtleBadge bg={item.tone} className="fs--2">
          {item.level}
        </SubtleBadge>
      </div>
    </div>
    <ProgressBar
      now={Math.min(Math.max(item.score, 0), 100)}
      variant={progressVariant(item.tone)}
      className="eeat-dimension-row__progress bg-200"
    />
  </div>
);

export const EeatDimensionsOverview = ({
  dimensions
}: {
  dimensions: EeatAnalysisData['dimensions'];
}) => (
  <div className="eeat-dimensions-list flex-grow-1 w-100">
    {dimensions.map(item => (
      <EeatDimensionRow key={item.key} item={item} />
    ))}
  </div>
);

export const EeatDimensionsOverviewPending = () => (
  <div className="eeat-dimensions-list flex-grow-1 w-100">
    {Array.from({ length: 4 }, (_, index) => (
      <div key={index} className="eeat-dimension-row eeat-dimension-row--pending">
        <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
          <div className="d-flex align-items-center gap-2 flex-grow-1">
            <TextLoading width={28} height={28} isWidthPx />
            <TextLoading width={36} height={10} />
          </div>
          <TextLoading width={48} height={14} isWidthPx />
        </div>
        <TextLoading width={100} height={4} />
      </div>
    ))}
  </div>
);
